/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import EventSequence from '../../@types/event-sequence';
import getPedigreeAbbrev from '../pedigree-utils';
import {FaultTree} from '../../@types/faultTree';
import {FaultTreeNode} from '../../@types/faultTreeNode';
import {faultTreeViewRegistry} from './views/fault-tree-view-registry';
import {FaultTreeViewType} from '../../@types/fault-tree-view-type';
import * as joint from '@joint/plus';
import {
    Barrier,
    BarrierContainer,
    BaseLink,
    ExpandButton,
    ExpandButtonLeft,
    ExpandButtonLeftWithGate,
    HouseNode,
    InitiatingNode,
    NegativeNode,
    NeutralNode,
    Node,
    PivotalNode,
    PositiveNode,
    Precursor,
    setCalloutIcon,
    setCalloutOpenForm,
    setOdnaStatIcons,
    setPsfNode,
    setTagIcons,
    TransferNode
} from '../isam-nodes'
import {toast} from 'react-toastify';
import {CanvasEnvironment} from './canvas-environment';
import {EsdEvent} from '../../@types/esdEvent';
import * as CanvasUtils from './node-utils';
import Tag from '../../@types/tag';
import {EventSequencePsfTemplateItemStats, PsfTemplate, PsfTemplateItemWithCount} from '../../@types/occurrence-dna';
import {profilerService} from '../../services/profiler.service';

// ====== PERFORMANCE PROFILER ======
// The profiler is now managed via ProfilerService and can be toggled from the UI
// Use View menu -> "Enable Profiling" to turn on, "Show Profiler" to see results

// Export a proxy object that delegates to profilerService for backward compatibility
export const profiler = {
    start: (contextLabel: string, metadata?: { elementCount?: number; nodeCount?: number; operation?: string }): void => {
        profilerService.start(contextLabel, metadata);
    },
    mark: (label: string): void => {
        profilerService.mark(label);
    },
    end: (): void => {
        profilerService.end();
    }
};

/**
 * Filters elements to only those visible in the current viewport.
 * Used for lazy decoration to improve performance on large graphs.
 */
export function filterVisibleElements(
    paperScroller: joint.ui.PaperScroller,
    elements: joint.dia.Element[],
    inflate: number = 50
): joint.dia.Element[] {
    const viewportArea = paperScroller.getVisibleArea().inflate(inflate);
    return elements.filter(el => {
        if (el.get('hidden')) return false;
        const bbox = el.getBBox();
        return viewportArea.intersect(bbox) !== null;
    });
}

export const allshapes = {
    ...joint.shapes,
    isam: {
        Node,
        Barrier,
        BarrierContainer,
        BaseLink,
        Precursor,
        NegativeNode,
        PositiveNode,
        PivotalNode,
        InitiatingNode,
        HouseNode,
        TransferNode
    }
};

function getProbOrFrequencyValueGivenMode(quantificationMode: string, ftNode: FaultTreeNode) {
    let qval = ((ftNode.nodeClass === 'PRECURSOR') || (ftNode.nodeClass === 'INDUCED')) ?
        ftNode.frequency : ftNode.probability;
    let label = ((ftNode.nodeClass === 'PRECURSOR') || (ftNode.nodeClass === 'INDUCED')) ? 'F' : 'P';
    if ((quantificationMode === 'MODE_PROBABILITY') &&
    ((ftNode.nodeClass === 'PRECURSOR') || (ftNode.nodeClass === 'INDUCED'))) {
        qval = ftNode.probability !== undefined ? ftNode.probability : ftNode.frequency;
        label = 'P';
    }
    if ((quantificationMode === 'MODE_FREQUENCY') &&
    ((ftNode.nodeClass === 'PRECURSOR') || (ftNode.nodeClass === 'INDUCED'))) {
        qval = ftNode.frequency !== undefined ? ftNode.frequency : ftNode.probability;
        label = 'F' ;
    }

    return { label, qval };
}

export function addOdnaStatToNodes(canvasEnvironment: CanvasEnvironment, elements: joint.dia.Element[], skipViewportFilter: boolean = false) {
    const { eventSequenceOccurrenceStats, paper, paperScroller, odnaStatByEventId, odnaStatByFtNodeId } = canvasEnvironment;

    if (!eventSequenceOccurrenceStats) return;

    // Filter to visible elements for performance, unless explicitly skipped
    const filteredElements = skipViewportFilter
        ? elements
        : filterVisibleElements(paperScroller, elements);

    for (const element of filteredElements) {
        const elementId = element.id as string;
        // Skip if already decorated
        if (canvasEnvironment.isOdnaElementDecorated(elementId)) continue;
        if (element.get('nodeClass') === 'CALLOUTNODE' || element.get('nodeClass') === 'PSFNODE') continue;

        // Use pre-built Maps for O(1) lookup instead of O(n) .find()
        const dataId = element.get('data').id;
        const odnaStat = CanvasUtils.isEvent(element.get('data').type)
            ? odnaStatByEventId.get(dataId)
            : odnaStatByFtNodeId.get(dataId);

        if (odnaStat) {
            setOdnaStatIcons(paper, element, odnaStat.numSuccesses, odnaStat.numFailures, eventSequenceOccurrenceStats.numOccurrences);
            canvasEnvironment.markOdnaElementDecorated(elementId);
        }
    }
}

export function clearOdnaStats(canvasEnvironment: CanvasEnvironment) {
    canvasEnvironment.graph.getElements().forEach((element) => {
        element.removeProp('odnaStatFailure');
        element.removeProp('odnaStatSuccess');
    });
}




export function addTagsToNode(canvasEnvironment: CanvasEnvironment, element: joint.dia.Element) {
    const { paper, tagLinksByEventId, tagLinksByFtNodeId, tagById } = canvasEnvironment;
    const dataId = element.get('data').id;

    // Use pre-built Maps for O(1) lookup instead of O(n) .filter()
    const tagIds = CanvasUtils.isEvent(element.get('data').type)
        ? tagLinksByEventId.get(dataId) || []
        : tagLinksByFtNodeId.get(dataId) || [];

    const tags = tagIds.map(id => tagById.get(id)).filter((tag): tag is Tag => tag !== undefined);
    setTagIcons(paper, element, tags);
}

export function addTagsToNodes(canvasEnvironment: CanvasEnvironment, elements: joint.dia.Element[], skipViewportFilter: boolean = false) {
    const { paper, paperScroller, tagLinksByEventId, tagLinksByFtNodeId, tagById } = canvasEnvironment;

    // Filter to visible elements for performance, unless explicitly skipped
    const filteredElements = skipViewportFilter
        ? elements
        : filterVisibleElements(paperScroller, elements);

    for (const element of filteredElements) {
        const elementId = element.id as string;
        // Skip if already decorated
        if (canvasEnvironment.isTagElementDecorated(elementId)) continue;
        if (element.get('nodeClass') === 'CALLOUTNODE' || element.get('nodeClass') === 'PSFNODE') continue;

        const dataId = element.get('data').id;
        // Use pre-built Maps for O(1) lookup instead of O(n) .filter()
        const tagIds = CanvasUtils.isEvent(element.get('data').type)
            ? tagLinksByEventId.get(dataId) || []
            : tagLinksByFtNodeId.get(dataId) || [];

        const tags = tagIds.map(id => tagById.get(id)).filter((tag): tag is Tag => tag !== undefined);
        setTagIcons(paper, element, tags);
        canvasEnvironment.markTagElementDecorated(elementId);
    }
}

export function toggleTagsMode(canvasEnvironment: CanvasEnvironment) {
    if (!canvasEnvironment.isTagsModeActive) {
        canvasEnvironment.fetchTagsAndTagLinks().then(() => {
            canvasEnvironment.isTagsModeActive = !canvasEnvironment.isTagsModeActive;
            addTagsToNodes(canvasEnvironment, canvasEnvironment.graph.getElements());
        }).catch((error) => {
            toast.error(error);
        });
    } else {
        canvasEnvironment.isTagsModeActive = !canvasEnvironment.isTagsModeActive;
        canvasEnvironment.graph.getElements().forEach((element) => {
            element.removeProp('tags');
        });
        canvasEnvironment.clearTagDecorationTracking();
    }
}

export function drawDnaPath(canvasEnvironment: CanvasEnvironment) {
    canvasEnvironment.drawDnaPath();
}

export function addPsfsToNode(canvasEnvironment: CanvasEnvironment, psfItems: PsfTemplate, psfCounts: EventSequencePsfTemplateItemStats,  element: joint.dia.Element) {
    const { graph, psfNodeIds } = canvasEnvironment;
    const faultTreeNodePsfTemplateItemStat = psfCounts.faultTreeNodePsfTemplateItemStats.find(
        (psfStat) => psfStat.faultTreeNodeId == element.get('data').id
    );

    // Use predictable PSF node ID for O(1) lookup instead of getNeighbors + filter
    const psfNodeId = 'psf-' + element.get('data').id;
    const existingPsf = graph.getCell(psfNodeId);
    if (existingPsf) {
        existingPsf.remove();
        psfNodeIds.delete(psfNodeId);
    }

    if (!faultTreeNodePsfTemplateItemStat) {
        canvasEnvironment.deletePsfTemplateItemCounts(element.get('data').id);
        return;
    }

    if (canvasEnvironment.occurrenceSelectedIds.length > 0 && canvasEnvironment.currentLinkedOccurrenceDna) {
        let find = canvasEnvironment.occurrenceSelectedIds.find((id) => id === canvasEnvironment.currentLinkedOccurrenceDna?.occurrenceId);
        if (!find) {
            return;
        }
    }

    let psfItemWithCounts = faultTreeNodePsfTemplateItemStat.psfTemplateItemStats.map((stat) => {
        // Use pre-built Map for O(1) lookup instead of O(n) .find()
        const psfItem = canvasEnvironment.psfTemplateItemById.get(Number(stat.psfTemplateItemId));
        return { ...psfItem, count: stat.numOccurrences } as PsfTemplateItemWithCount;
    });

    if (canvasEnvironment.psfSelectedIds.length > 0) {
        psfItemWithCounts = psfItemWithCounts.filter((psf) => canvasEnvironment.psfSelectedIds.includes(psf.id));
    }
    canvasEnvironment.addPsfTemplateItemCounts(element.get('data').id, psfItemWithCounts);
    setPsfNode(canvasEnvironment,  element, psfItemWithCounts);
}

export function clearAllPsfs(canvasEnvironment: CanvasEnvironment) {
    const { graph, psfNodeIds } = canvasEnvironment;
    // Use cached PSF node IDs for O(1) lookup instead of O(n) graph scan
    for (const psfId of psfNodeIds) {
        const psfNode = graph.getCell(psfId);
        if (psfNode) {
            psfNode.remove();
        }
    }
    psfNodeIds.clear();
}

export function drawAllPsfs(canvasEnvironment: CanvasEnvironment) {
    const { psfCounts, graph } = canvasEnvironment;
    clearAllPsfs(canvasEnvironment);
    psfCounts.forEach((psfItemCount, nodeId) => {
        let node = graph.getCell('ft-'+ nodeId) as joint.dia.Element;
        if (node) {
            setPsfNode(canvasEnvironment, node, psfItemCount);
        }
    });
}

export function toggleOdnaMode(canvasEnvironment: CanvasEnvironment) {
    if (canvasEnvironment.isOdnaModeActive) {
        // Use cached PSF node IDs instead of full graph scan
        clearAllPsfs(canvasEnvironment);
        canvasEnvironment.setLinkedOccurrenceDna(null);
        canvasEnvironment.clearAllPsfTemplateItemCounts();
    }
    canvasEnvironment.isOdnaModeActive = !canvasEnvironment.isOdnaModeActive;
}

export function clearPsfMode(canvasEnvironment: CanvasEnvironment) {
    if (canvasEnvironment.isOdnaModeActive) {
        // Use cached PSF node IDs instead of full graph scan
        clearAllPsfs(canvasEnvironment);
        canvasEnvironment.clearAllPsfTemplateItemCounts();
    }
}


export function addCalloutsToNodes(canvasEnvironment: CanvasEnvironment, elements: joint.dia.Element[], skipViewportFilter: boolean = false) {
    const { graph, paper, paperScroller } = canvasEnvironment;

    // Filter to visible elements for performance, unless explicitly skipped
    const filteredElements = skipViewportFilter
        ? elements
        : filterVisibleElements(paperScroller, elements);

    filteredElements.forEach((element) => {
        const elementId = element.id as string;
        // Skip if already decorated
        if (canvasEnvironment.isCalloutElementDecorated(elementId)) return;
        if (canvasEnvironment.isCalloutsModeActive) {
            setCalloutOpenForm(graph, paper, element);

        } else {
            setCalloutIcon(paper, element);
        }
        canvasEnvironment.markCalloutElementDecorated(elementId);
    });
}

export function toggleCalloutsMode(canvasEnvironment: CanvasEnvironment) {
    if (canvasEnvironment.isCalloutsModeActive) {
        let callouts = canvasEnvironment.graph.getElements().filter((element) => element.get('nodeClass') === 'CALLOUTNODE');
        callouts.forEach((callout) => {
            callout.remove();
        });
    } else {
        canvasEnvironment.graph.getElements().forEach((element) => {
            element.removeProp('callout');
        });
    }
    canvasEnvironment.isCalloutsModeActive = !canvasEnvironment.isCalloutsModeActive;
    canvasEnvironment.clearCalloutDecorationTracking();
    addCalloutsToNodes(canvasEnvironment, canvasEnvironment.graph.getElements());
}


export function addTools(
    paper: joint.dia.Paper,
    elements: joint.dia.Element[],
    viewType?: FaultTreeViewType,
    graph?: joint.dia.Graph
): void {
    const toolName = 'expand-tools';
    const strategy = viewType ? faultTreeViewRegistry.get(viewType) : null;

    elements.forEach((element) => {
        const nodeClass = element.get('nodeClass');
        if (nodeClass === 'CALLOUTNODE' || nodeClass === 'PSFNODE') return;
        if (nodeClass === 'FAULTTREENODE' && !element.get('data').childIds) return;
        if (nodeClass !== 'FAULTTREENODE' && !element.get('data').faultTree) return;
        const view = element.findView(paper);
        // Guard: view may be undefined if element is not rendered (e.g., during RAF timing)
        if (!view || view.hasTools(toolName)) return;

        // Use strategy hook to determine button configuration
        const buttonConfig = strategy?.getExpandButtonConfig?.(element, graph!);

        let ButtonClass = ExpandButton;
        if (buttonConfig?.position === 'left') {
            ButtonClass = buttonConfig.hasGate ? ExpandButtonLeftWithGate : ExpandButtonLeft;
        }

        const toolsView = new joint.dia.ToolsView({
            name: toolName,
            tools: [new ButtonClass()]
        });
        view.addTools(toolsView);
    })
}

/**
 * Batched decoration function that applies all decorations in a single pass.
 * Filters to visible elements first for better performance on large graphs.
 */
export function decorateVisibleElements(
    canvasEnvironment: CanvasEnvironment,
    options: {
        addToolsFlag?: boolean;
        skipViewportFilter?: boolean;
    } = {}
): void {
    const { paper, paperScroller, graph } = canvasEnvironment;
    const { addToolsFlag = true, skipViewportFilter = false } = options;

    // Get all elements once
    const allElements = graph.getElements();

    // Filter to visible elements unless explicitly skipped
    const elements = skipViewportFilter
        ? allElements
        : filterVisibleElements(paperScroller, allElements);

    // Apply all decorations in a single pass
    elements.forEach((element) => {
        const elementId = element.id as string;
        const nodeClass = element.get('nodeClass');

        // Skip special nodes
        if (nodeClass === 'CALLOUTNODE' || nodeClass === 'PSFNODE') return;

        // Add tools if needed
        if (addToolsFlag) {
            let hasExpandableContent: boolean;
            if (nodeClass === 'BARRIERCONTAINER') {
                // BarrierContainer is expandable if it has embedded barriers
                const embeddedBarrierIds = element.get('embeddedBarrierIds') as string[] | undefined;
                hasExpandableContent = !!embeddedBarrierIds && embeddedBarrierIds.length > 0;
            } else if (nodeClass === 'FAULTTREENODE') {
                hasExpandableContent = !!element.get('data')?.childIds;
            } else {
                hasExpandableContent = !!element.get('data')?.faultTree;
            }
            if (hasExpandableContent) {
                const view = element.findView(paper);
                if (view && !view.hasTools('expand-tools')) {
                    // Use strategy hook to determine button configuration
                    const viewType = canvasEnvironment.currentViewType;
                    const strategy = faultTreeViewRegistry.get(viewType);
                    const buttonConfig = strategy.getExpandButtonConfig?.(element, graph);

                    let ButtonClass = ExpandButton;
                    if (buttonConfig?.position === 'left') {
                        ButtonClass = buttonConfig.hasGate ? ExpandButtonLeftWithGate : ExpandButtonLeft;
                    }

                    const toolsView = new joint.dia.ToolsView({
                        name: 'expand-tools',
                        tools: [new ButtonClass()]
                    });
                    view.addTools(toolsView);
                }
            }
        }

        // Skip BarrierContainers for ODNA stats, tags, and callouts - they don't have data property
        if (nodeClass === 'BARRIERCONTAINER') return;

        // Add ODNA stats if not already decorated - use pre-built Maps for O(1) lookup
        if (!canvasEnvironment.isOdnaElementDecorated(elementId) &&
            canvasEnvironment.eventSequenceOccurrenceStats) {
            const dataId = element.get('data').id;
            const stats = CanvasUtils.isEvent(element.get('data').type)
                ? canvasEnvironment.odnaStatByEventId.get(dataId)
                : canvasEnvironment.odnaStatByFtNodeId.get(dataId);
            if (stats) {
                setOdnaStatIcons(paper, element, stats.numSuccesses, stats.numFailures, canvasEnvironment.eventSequenceOccurrenceStats.numOccurrences);
                canvasEnvironment.markOdnaElementDecorated(elementId);
            }
        }

        // Add tags if tags mode is active and not already decorated - use pre-built Maps for O(1) lookup
        if (canvasEnvironment.isTagsModeActive && !canvasEnvironment.isTagElementDecorated(elementId)) {
            const dataId = element.get('data').id;
            const tagIds = CanvasUtils.isEvent(element.get('data').type)
                ? canvasEnvironment.tagLinksByEventId.get(dataId) || []
                : canvasEnvironment.tagLinksByFtNodeId.get(dataId) || [];
            const tags = tagIds.map(id => canvasEnvironment.tagById.get(id)).filter((tag): tag is Tag => tag !== undefined);
            setTagIcons(paper, element, tags);
            canvasEnvironment.markTagElementDecorated(elementId);
        }

        // Add callouts if not already decorated
        if (!canvasEnvironment.isCalloutElementDecorated(elementId)) {
            if (canvasEnvironment.isCalloutsModeActive) {
                setCalloutOpenForm(graph, paper, element);
            } else {
                setCalloutIcon(paper, element);
            }
            canvasEnvironment.markCalloutElementDecorated(elementId);
        }
    });

    // Track which view type was used for tool rendering
    if (addToolsFlag) {
        canvasEnvironment.renderedViewType = canvasEnvironment.currentViewType;
    }
}

/**
 * Re-adds expand tools to all elements when the view type changes.
 * This is needed when switching tabs back to a canvas that was rendered
 * with a different view type.
 */
export function updateToolsForViewType(canvasEnvironment: CanvasEnvironment): void {
    const { paper, graph } = canvasEnvironment;
    const toolName = 'expand-tools';
    const viewType = canvasEnvironment.currentViewType;
    const strategy = faultTreeViewRegistry.get(viewType);

    // Remove existing tools and re-add with correct button type
    graph.getElements().forEach((element) => {
        const nodeClass = element.get('nodeClass');
        if (nodeClass === 'CALLOUTNODE' || nodeClass === 'PSFNODE') return;

        let hasExpandableContent: boolean;
        if (nodeClass === 'BARRIERCONTAINER') {
            const embeddedBarrierIds = element.get('embeddedBarrierIds') as string[] | undefined;
            hasExpandableContent = !!embeddedBarrierIds && embeddedBarrierIds.length > 0;
        } else if (nodeClass === 'FAULTTREENODE') {
            hasExpandableContent = !!element.get('data')?.childIds;
        } else {
            hasExpandableContent = !!element.get('data')?.faultTree;
        }

        if (hasExpandableContent) {
            const view = element.findView(paper);
            if (view) {
                // Remove existing tools
                if (view.hasTools(toolName)) {
                    view.removeTools();
                }

                // Use strategy hook to determine button configuration
                const buttonConfig = strategy.getExpandButtonConfig?.(element, graph);

                let ButtonClass = ExpandButton;
                if (buttonConfig?.position === 'left') {
                    ButtonClass = buttonConfig.hasGate ? ExpandButtonLeftWithGate : ExpandButtonLeft;
                }

                const toolsView = new joint.dia.ToolsView({
                    name: toolName,
                    tools: [new ButtonClass()]
                });
                view.addTools(toolsView);
            }
        }
    });

    // Update rendered view type tracking
    canvasEnvironment.renderedViewType = canvasEnvironment.currentViewType;
}

export function resizePaper(paper: joint.dia.Paper) {
    paper.fitToContent({
        useModelGeometry: true,
        allowNewOrigin: 'any',
        allowNegativeBottomRight: true,
        padding: { top: 50, left: 480 },
        contentArea: new joint.g.Rect(0, 0, 4000, 3000).union(paper.model.getBBox()!),

    });
}

export function layoutAndFocus(treeLayout: joint.layout.TreeLayout, paperScroller: joint.ui.PaperScroller, paper: joint.dia.Paper,focusPoint?: { x: number, y: number }) {
    treeLayout.layout();
    var center = focusPoint || treeLayout.getLayoutBBox()!.center();
    resizePaper(paper);
    paperScroller.center(center.x, center.y);
}



export function buildESDGraphFromObject(eventSequence: EventSequence, cellsGraph: joint.dia.Graph): joint.dia.Graph {
    const parentChildrenMap = new Map<string, string[]>();

    cellsGraph.resetCells([]);
    if (!eventSequence || !eventSequence.events) return cellsGraph;
    const eventSequenceUniqueId = eventSequence.uniqueId;
    try {
        eventSequence.events.forEach(event => {
            let eventUniqueId = event.uniqueId.replace(eventSequenceUniqueId, '');
            let qval = ['INITIATING', 'END', 'INPRECURSOR'].includes(event.type) ? event.frequency : event.probability;
            let label = ['INITIATING', 'END', 'INPRECURSOR'].includes(event.type) ? 'F' : 'P';
            let pedigree = getPedigreeAbbrev(event.pedigree);
            let qvalLabel = `${label}=${Number.parseFloat(qval).toExponential(2)}`;
            let bias = '';
            let cictt = event.occurrenceCategory ?? '';
            let node;
            if (event.type === 'INITIATING') {
                node = InitiatingNode.create(eventUniqueId, event.name, qvalLabel, pedigree, bias, cictt);
            } else if (event.type === 'PIVOTAL') {
                node = PivotalNode.create(eventUniqueId, event.name, qvalLabel, pedigree, bias, cictt);
            } else if (event.type === 'END') {
                if (event.outcome === 'POSITIVE') {
                    node = PositiveNode.create(eventUniqueId, event.name, qvalLabel, pedigree, bias, cictt);
                } else if (event.outcome === 'NEGATIVE') {
                    node = NegativeNode.create(eventUniqueId, event.name, qvalLabel, pedigree, bias, cictt);
                } else {
                    node = NeutralNode.create(eventUniqueId, event.name, qvalLabel, pedigree, bias, cictt);
                }
            } else {
                throw new Error('Unknown event type: ' + event.type);
            }
            // Batch property updates into single set() call for efficiency
            node.set({
                data: event,
                id: 'event-' + event.id,
                nodeClass: 'EVENTNODE',
                // Set collapsed: true so expand button shows '+' (FT is not expanded initially)
                collapsed: true
            });
            node.attr('body/title', event.name);
            cellsGraph.addCell(node);
            if (event.type === 'INITIATING') {
                parentChildrenMap.set('event-' + event.id, []);
            } else {
                let parent = parentChildrenMap.get('event-' + event.parentId);
                if (parent) {
                    parent.push('event-' + event.id);
                } else {
                    parentChildrenMap.set('event-' + event.parentId, ['event-' + event.id]);
                }
            }
        });

        parentChildrenMap.forEach((children, parentId) => {
            const parent = cellsGraph.getCell(parentId);
            if (parent) {
                children.forEach((childId) => {
                    const child = cellsGraph.getCell(childId);
                    if (child) {
                        const link = BaseLink.create(parent, child, child.get('data').parentRelationType);
                        link.set('id', 'link-' + parent.get('id') + '-' + child.get('id'));
                        cellsGraph.addCell(link);
                    }
                });
            }
        });

    } catch (e: any) {
        // eslint-disable-next-line no-console
        console.error('Error building ESDE events: ', e);
        toast.error(e.message);
    }


    return cellsGraph;
}

// Lazy loading configuration - set to Infinity to disable lazy loading
const LAZY_LOAD_INITIAL_DEPTH = 3;

export function buildftGraphFromObject(
    eventSequence: EventSequence,
    faultTree: FaultTree,
    maxDepth: number = LAZY_LOAD_INITIAL_DEPTH,
    expandedLazyNodeIds: Set<number> = new Set(),
    useHorizontalAnchors: boolean = false
): joint.dia.Graph {
    const cellsGraph = new joint.dia.Graph({}, { cellNamespace: allshapes });
    const parentChildrenMap = new Map<string, string[]>();

    if (!eventSequence || !eventSequence.events) return cellsGraph;

    const eventSequenceUniqueId = eventSequence.uniqueId;

    if (!faultTree) return cellsGraph;
    let faultTreeNodes: Array<FaultTreeNode> = faultTree.faultTreeNodes;
    let quantifcationMode = eventSequence.quantificationMode;
    if (!faultTreeNodes) return cellsGraph;


    try {
        // Build lookup map for FaultTreeNodes by id
        const ftNodeById = new Map<number, FaultTreeNode>();
        faultTreeNodes.forEach((ftNode) => {
            ftNodeById.set(ftNode.id, ftNode);
        });

        // Cleanup: remove stale IDs from expandedLazyNodeIds (e.g., deleted nodes)
        const staleIds: number[] = [];
        for (const expandedId of expandedLazyNodeIds) {
            if (!ftNodeById.has(expandedId)) {
                staleIds.push(expandedId);
            }
        }
        for (const staleId of staleIds) {
            expandedLazyNodeIds.delete(staleId);
        }

        // Calculate depth of each node via BFS from root
        const depthMap = new Map<number, number>();
        const rootId = faultTree.rootNodeId;
        if (rootId && ftNodeById.has(rootId)) {
            const queue: Array<{ id: number; depth: number }> = [{ id: rootId, depth: 0 }];
            while (queue.length > 0) {
                const { id, depth } = queue.shift()!;
                if (depthMap.has(id)) continue;
                depthMap.set(id, depth);
                const node = ftNodeById.get(id);
                if (node && node.childIds) {
                    for (const childId of node.childIds) {
                        if (!depthMap.has(childId)) {
                            queue.push({ id: childId, depth: depth + 1 });
                        }
                    }
                }
            }
        }

        // Determine which nodes should be loaded based on:
        // 1. Within maxDepth
        // 2. Previously expanded (in expandedLazyNodeIds)
        // 3. Child of a previously expanded node
        // 4. On the path from root to any expanded node
        const nodesToLoad = new Set<number>();
        faultTreeNodes.forEach((ftNode) => {
            const depth = depthMap.get(ftNode.id) ?? 0;
            if (depth <= maxDepth) {
                nodesToLoad.add(ftNode.id);
            }
        });

        // For each expanded node, add the entire path from root to it, plus its children
        for (const expandedId of expandedLazyNodeIds) {
            const expandedNode = ftNodeById.get(expandedId);
            if (!expandedNode) continue;

            // Add the expanded node and its children
            nodesToLoad.add(expandedId);
            if (expandedNode.childIds) {
                for (const childId of expandedNode.childIds) {
                    nodesToLoad.add(childId);
                }
            }

            // Add all ancestors (path from root to this node)
            let currentId: number | undefined = expandedNode.parentId;
            while (currentId !== undefined) {
                nodesToLoad.add(currentId);
                const parentNode = ftNodeById.get(currentId);
                currentId = parentNode?.parentId;
            }
        }


        // Collect all nodes for batch addition
        const nodes: joint.dia.Cell[] = [];
        const nodeById = new Map<string, joint.dia.Cell>();

        faultTreeNodes.forEach((ftNode : FaultTreeNode) => {
            // Skip nodes not in the load set
            if (!nodesToLoad.has(ftNode.id)) return;

            const depth = depthMap.get(ftNode.id) ?? 0;

            let nodeUniqueId = ftNode.uniqueId.replace(eventSequenceUniqueId, '');
            let nodeType = ftNode.nodeClass;
            let bias = ftNode.bias;
            let pedigree = getPedigreeAbbrev(ftNode.pedigree);
            let { label, qval } = getProbOrFrequencyValueGivenMode(quantifcationMode, ftNode);
            let qvalLabel = `${label}=${Number.parseFloat(qval.toString()).toExponential(2)}`;

            let node;
            if (nodeType === 'PRECURSOR') {
                node = Precursor.create(nodeUniqueId, ftNode.name, qvalLabel, pedigree, `B=${bias}`, ftNode.occurrenceCategory ?? '');
            } else if (nodeType === 'BARRIER') {
                node = Barrier.create(nodeUniqueId, ftNode.name, qvalLabel, pedigree, `B=${bias}`, ftNode.occurrenceCategory ?? '');
            } else {
                node = Node.create(nodeUniqueId, ftNode.name, qvalLabel, pedigree, `B=${bias}`, ftNode.occurrenceCategory ?? '');
            }
            if (ftNode.type !== '') {
                node.gate(ftNode.type.toLowerCase());
            }

            // Check if this node has children that are NOT loaded
            // A node has unloaded children if it has children and any child is not in nodesToLoad
            let hasUnloadedChildren = false;
            if (ftNode.childIds && ftNode.childIds.length > 0) {
                hasUnloadedChildren = ftNode.childIds.some(childId => !nodesToLoad.has(childId));
            }

            // Batch property updates into single set() call for efficiency
            const nodeId = 'ft-' + ftNode.id;
            node.set({
                data: ftNode,
                id: nodeId,
                nodeClass: 'FAULTTREENODE',
                hasUnloadedChildren: hasUnloadedChildren,
                collapsed: hasUnloadedChildren  // Start collapsed if has unloaded children
            });
            node.attr('body/title', ftNode.name);

            // Collect node for batch add (instead of individual addCell)
            nodes.push(node);
            nodeById.set(nodeId, node);

            // Only create parent-child links if parent is also loaded
            if (ftNode.parentId && nodesToLoad.has(ftNode.parentId)) {
                const parentId = 'ft-' + ftNode.parentId;
                let parent = parentChildrenMap.get(parentId);
                if (parent) {
                    parent.push(nodeId);
                } else {
                    parentChildrenMap.set(parentId, [nodeId]);
                }
            }
        });

        // Batch add all nodes at once (much faster than individual addCell calls)
        cellsGraph.addCells(nodes, { dry: true });

        // Track embedded barriers and containers (for Bowtie view)
        const embeddedBarrierIds = new Set<string>();
        const containerByParent = new Map<string, joint.dia.Element>();

        // In Bowtie view (useHorizontalAnchors), group barrier children into containers
        // But only if the parent is NOT a barrier itself
        if (useHorizontalAnchors) {
            parentChildrenMap.forEach((childIds, parentId) => {
                // Skip if parent is a barrier - don't group barrier's children
                const parentElement = nodeById.get(parentId) as joint.dia.Element;
                const parentIsBarrier = parentElement?.get('data')?.nodeClass === 'BARRIER';
                if (parentIsBarrier) return;

                // Find barrier children
                const barrierChildIds = childIds.filter(childId => {
                    const child = nodeById.get(childId) as joint.dia.Element;
                    return child?.get('data')?.nodeClass === 'BARRIER';
                });

                if (barrierChildIds.length > 0) {
                    // Create BarrierContainer with adaptive height based on gates
                    const gateTypes = ['or', 'and', 'xor', 'priority_and', 'inhibit', 'transfer'];
                    const baseHeight = 150;
                    const gateExtra = 70;
                    const buttonExtra = 30;
                    const gap = 15;
                    const padding = 10;

                    // Calculate height for each embedded node
                    const nodeHeights = barrierChildIds.map(childId => {
                        const child = nodeById.get(childId) as joint.dia.Element;
                        const nodeType = child?.get('data')?.type?.toLowerCase();
                        const hasGate = nodeType && gateTypes.includes(nodeType);
                        return baseHeight + buttonExtra + (hasGate ? gateExtra : 0);
                    });

                    const totalHeight = padding * 2 +
                        nodeHeights.reduce((sum, h) => sum + h, 0) +
                        (barrierChildIds.length - 1) * gap;

                    const containerWidth = 380;
                    const container = new BarrierContainer({ size: { width: containerWidth, height: totalHeight } });
                    const containerId = 'barrier-container-' + parentId;
                    container.set('id', containerId);
                    container.set('nodeClass', 'BARRIERCONTAINER');
                    container.set('parentNodeId', parentId);
                    // Store barrier info on container for positioning after layout
                    container.set('embeddedBarrierIds', barrierChildIds);
                    container.set('nodeHeights', nodeHeights);
                    container.set('barrierGap', gap);
                    container.set('barrierPadding', padding);
                    cellsGraph.addCell(container);
                    containerByParent.set(parentId, container);

                    // Mark barriers as embedded (they'll be positioned after layout)
                    barrierChildIds.forEach((barrierId) => {
                        const barrierElement = nodeById.get(barrierId) as joint.dia.Element;
                        if (barrierElement) {
                            barrierElement.set('containerId', containerId);
                            embeddedBarrierIds.add(barrierId);
                        }
                    });
                }
            });
        }

        // Collect all links for batch addition
        const links: joint.dia.Cell[] = [];

        parentChildrenMap.forEach((children, parentId) => {
            const parent = nodeById.get(parentId);
            if (!parent) return;

            // Check if this parent has a barrier container
            const container = containerByParent.get(parentId);

            if (container) {
                // Link parent -> container
                const linkToContainer = BaseLink.create(parent, container, undefined, { useHorizontalAnchors });
                linkToContainer.set('id', 'link-' + parentId + '-container');
                links.push(linkToContainer);

                // Non-barrier children link to the container
                children.forEach((childId) => {
                    if (embeddedBarrierIds.has(childId)) return; // Skip embedded barriers
                    const child = nodeById.get(childId);
                    if (child) {
                        const link = BaseLink.create(container, child, undefined, { useHorizontalAnchors });
                        link.set('id', 'link-container-' + parentId + '-' + childId);
                        links.push(link);
                    }
                });
            } else {
                // No barriers - link children directly to parent as before
                children.forEach((childId) => {
                    const child = nodeById.get(childId);
                    if (child) {
                        const link = BaseLink.create(parent, child, undefined, { useHorizontalAnchors });
                        link.set('id', 'link-' + parentId + '-' + childId);

                        // If parent is embedded, mark link for visual repositioning
                        const parentContainerId = parent.get('containerId');
                        if (parentContainerId) {
                            link.set('embeddedSourceContainerId', parentContainerId);
                        }

                        links.push(link);
                    }
                });
            }
        });

        // Batch add all links at once (much faster than individual addCell calls)
        cellsGraph.addCells(links, { dry: true });

    } catch (e: any) {
        // eslint-disable-next-line no-console
        console.error('Error building fault tree: ', e);
        toast.error(e.message);
    }

    return cellsGraph;
}


export function toDendogram(graph: joint.dia.Graph<joint.dia.Graph.Attributes, joint.dia.ModelSetOptions>, source: joint.dia.Element<joint.dia.Element.Attributes, joint.dia.ModelSetOptions>) {
    const elementCount = graph.getElements().length;
    const sourceName = source.get('data')?.name || 'Unknown';
    profiler.start(`toDendogram: ${sourceName} (${elementCount} elements)`);

    profiler.mark('Build neighbor map');
    const elements: Array<joint.dia.Element> = [];
    const xs: Array<number> = [];

    // Pre-compute neighbor map for O(1) lookups (instead of O(n) per element)
    const neighborMap = new Map<string, joint.dia.Element[]>();
    graph.getElements().forEach(el => {
        const neighbors = graph.getNeighbors(el, { outbound: true })
            .filter((cell) => cell.get('nodeClass') !== 'CALLOUTNODE' && cell.get('nodeClass') !== 'PSFNODE');
        neighborMap.set(el.id as string, neighbors);
    });

    profiler.mark('BFS traversal');
    // BFS traversal - collect non-fault-tree elements
    graph.bfs(source, (element: joint.dia.Element): boolean => {
        if (element.get('nodeClass') !== 'FAULTTREENODE') {
            elements.push(element);
            xs.push(element.position().x);
        }
        return true;
    });

    profiler.mark('Reposition elements');
    const maxX = Math.max(...xs);

    // Reposition elements using cached neighbor lookups
    elements.reverse().forEach((element) => {
        const children = neighborMap.get(element.id as string) || [];
        if (children.length > 0) {
            element.position(element.position().x, children[0].position().y);
        } else {
            element.position(maxX, element.position().y);
        }
    });

    profiler.mark('Process link vertices');
    // Process links - graph.getCell() is already O(1) lookup
    graph.getLinks().forEach((link) => {
        const sourceId = link.source().id;
        const targetId = link.target().id;
        if (!sourceId || !targetId) return;

        const sourceCell = graph.getCell(sourceId);
        const targetCell = graph.getCell(targetId);
        if (!sourceCell || !targetCell) return;

        const sourceCenter = sourceCell.getBBox().center();
        const targetCenter = targetCell.getBBox().center();
        if (sourceCenter.y === targetCenter.y) {
            link.vertices([]);
        } else {
            link.vertices([
                { x: sourceCenter.x + 200, y: sourceCenter.y },
                { x: sourceCenter.x + 200, y: targetCenter.y }
            ]);
        }
    });

    profiler.end();
}

/**
 * Position barriers inside their containers after layout.
 * This is called after layout runs to place embedded barriers
 * at the correct positions inside their container.
 */
export function positionBarriersInContainers(graph: joint.dia.Graph): void {
    graph.getElements().forEach((element) => {
        if (element.get('nodeClass') !== 'BARRIERCONTAINER') return;

        const embeddedBarrierIds = element.get('embeddedBarrierIds') as string[] | undefined;
        if (!embeddedBarrierIds || embeddedBarrierIds.length === 0) return;

        // Use individual node heights if available, otherwise fall back to fixed height
        const nodeHeights = element.get('nodeHeights') as number[] | undefined;
        const defaultHeight = 250;
        const gap = element.get('barrierGap') as number || 15;
        const padding = element.get('barrierPadding') as number || 10;

        const containerPos = element.position();
        let cumulativeY = padding;

        embeddedBarrierIds.forEach((barrierId, index) => {
            // Handle both formats: 'ft-123' (from buildftGraphFromObject) and '123' (from bowtie view)
            const nodeId = barrierId.startsWith('ft-') ? barrierId : 'ft-' + barrierId;
            const barrierElement = graph.getCell(nodeId) as joint.dia.Element;
            if (barrierElement) {
                // Position barrier inside the container
                // Use larger left offset to give space for container's expand button
                const xOffset = 50;
                barrierElement.position(containerPos.x + xOffset, containerPos.y + cumulativeY);

                // Add this node's height plus gap for next node
                const nodeHeight = nodeHeights?.[index] ?? defaultHeight;
                cumulativeY += nodeHeight + gap;

                // On initial load, collapse embedded barriers (hide their children and links)
                // Only do this once - check if we've already initialized this barrier
                if (!barrierElement.get('embeddedInitialized')) {
                    barrierElement.set('embeddedInitialized', true);
                    barrierElement.set('collapsed', true);

                    // Hide successors and their connected links
                    const successors = graph.getSuccessors(barrierElement);
                    successors.forEach(successor => {
                        successor.set({ hidden: true });

                        // Hide links connected to this successor
                        const connectedLinks = graph.getConnectedLinks(successor);
                        connectedLinks.forEach(link => {
                            link.set({ hidden: true });
                        });

                        // Also hide embedded barriers of any downstream containers
                        if (successor.get('nodeClass') === 'BARRIERCONTAINER') {
                            const nestedBarrierIds = successor.get('embeddedBarrierIds') as string[] | undefined;
                            if (nestedBarrierIds) {
                                nestedBarrierIds.forEach(nestedId => {
                                    const nestedNodeId = nestedId.startsWith('ft-') ? nestedId : 'ft-' + nestedId;
                                    const nestedBarrier = graph.getCell(nestedNodeId);
                                    if (nestedBarrier) {
                                        nestedBarrier.set({ hidden: true });
                                    }
                                });
                            }
                        }
                    });
                }
            }
        });
    });
}

/**
 * Layout embedded barrier subtrees vertically (top to bottom).
 * When an embedded barrier is expanded, its children should flow downward
 * like in Standard view, positioned below the BarrierContainer.
 */
export function layoutEmbeddedBarrierSubtrees(graph: joint.dia.Graph): void {
    // Find all expanded embedded barriers
    graph.getElements().forEach((barrier) => {
        // Must be an embedded barrier (has containerId) and not collapsed
        const containerId = barrier.get('containerId');
        if (!containerId || barrier.get('collapsed')) return;

        // Get visible successors of this embedded barrier
        const successors = graph.getSuccessors(barrier).filter(
            (cell): cell is joint.dia.Element => cell.isElement() && !cell.get('hidden')
        );

        if (successors.length === 0) return;

        // Find the BarrierContainer
        const container = graph.getCell(containerId) as joint.dia.Element;
        if (!container) return;

        // Create a temporary graph for layout
        const subtreeGraph = new joint.dia.Graph({}, { cellNamespace: allshapes });

        // Add the barrier and its visible successors to the temp graph
        const subtreeCells = [barrier, ...successors];
        subtreeGraph.resetCells(graph.getSubgraph(subtreeCells));

        // Apply vertical TreeLayout (direction: 'B' for bottom)
        const treeLayout = new joint.layout.TreeLayout({
            graph: subtreeGraph,
            direction: 'B',
            siblingGap: 50,
            parentGap: 150,
        });
        treeLayout.layout();

        // Get container's bottom position
        const containerPos = container.position();
        const containerSize = container.size();
        const containerBottom = containerPos.y + containerSize.height;

        // Get the barrier's position in the layout
        const barrierPos = barrier.position();
        const layoutedBarrier = subtreeGraph.getCell(barrier.id) as joint.dia.Element;
        const layoutedBarrierPos = layoutedBarrier.position();

        // Calculate offset: X stays relative to barrier, Y positions children below container
        const offsetX = barrierPos.x - layoutedBarrierPos.x;

        // Find the topmost child position in the layout (relative to barrier)
        let minChildY = Infinity;
        successors.forEach((successor) => {
            const layoutedSuccessor = subtreeGraph.getCell(successor.id) as joint.dia.Element;
            if (layoutedSuccessor) {
                minChildY = Math.min(minChildY, layoutedSuccessor.position().y);
            }
        });

        // Offset Y so children start below the container (with a gap)
        const gap = 60;
        const offsetY = containerBottom + gap - minChildY;

        // Apply the calculated positions to the original graph
        successors.forEach((successor) => {
            const layoutedSuccessor = subtreeGraph.getCell(successor.id) as joint.dia.Element;
            if (layoutedSuccessor) {
                const layoutedPos = layoutedSuccessor.position();
                successor.position(layoutedPos.x + offsetX, layoutedPos.y + offsetY);
            }
        });

        // Update link source anchors to visually start from container bottom
        const outboundLinks = graph.getConnectedLinks(barrier, { outbound: true });
        //const containerCenter = containerPos.x + containerSize.width / 2;

        outboundLinks.forEach(link => {
            // Keep barrier as logical source, but visually anchor at container's bottom
            // Use 'bottom' anchor with dx/dy to position at container's bottom center
            link.source({
                id: barrier.id,
                anchor: {
                    name: 'bottom',
                    args: {
                        dx: 0,
                        dy: containerBottom - barrierPos.y  - barrier.size().height
                    }
                }
            });

            link.target({
                id: link.getTargetCell()?.id,
                anchor: {
                    name: 'top'
                }
            })
            
        });
    });
}

export function createFtGraphAndExpand(canvasEnvironment: CanvasEnvironment, element: joint.dia.Element, graph: joint.dia.Graph, paper: joint.dia.Paper, paperScroller: joint.ui.PaperScroller, viewportRect: any) {
    const elementData = element.get('data');
    const ftName = elementData?.name || elementData?.faultTree?.name || 'Unknown FT';
    const ftNodeCount = elementData?.faultTree?.faultTreeNodes?.length || 0;
    profiler.start(`createFtGraphAndExpand: ${ftName} (${ftNodeCount} nodes)`);

    // Increment generation to invalidate any pending async callbacks from previous expand/collapse
    canvasEnvironment.expansionGeneration++;
    const currentGeneration = canvasEnvironment.expansionGeneration;

    // Clean up any orphaned FT cells from previous sessions (e.g., after tab switch)
    // Use resetCells approach because BarrierContainer cells may not have proper graph references
    const existingFtElements = graph.getElements().filter(cell => {
        const nodeClass = cell.get('nodeClass');
        const cellId = cell.id as string;
        return nodeClass === 'FAULTTREENODE' ||
               nodeClass === 'BARRIERCONTAINER' ||
               cellId?.startsWith('barrier-container-');
    });
    if (existingFtElements.length > 0) {
        const ftElementIds = new Set(existingFtElements.map(c => c.id));
        // Also identify links connected to FT elements
        const linksToRemove = graph.getLinks().filter(link => {
            const sourceId = link.get('source')?.id;
            const targetId = link.get('target')?.id;
            return ftElementIds.has(sourceId) || ftElementIds.has(targetId);
        });
        const linkIdsToRemove = new Set(linksToRemove.map(link => link.id));
        // Keep cells that are not FT elements and not links to FT elements
        const cellsToKeep = graph.getCells().filter(cell => {
            return !ftElementIds.has(cell.id) && !linkIdsToRemove.has(cell.id);
        });
        graph.resetCells(cellsToKeep);
    }

    // Get or create the expanded nodes set for this specific event's fault tree
    const eventId = element.get('data')?.id as number;
    if (!canvasEnvironment.expandedLazyNodeIdsByEvent.has(eventId)) {
        canvasEnvironment.expandedLazyNodeIdsByEvent.set(eventId, new Set());
    }
    const expandedLazyNodeIds = canvasEnvironment.expandedLazyNodeIdsByEvent.get(eventId)!;

    profiler.mark('buildftGraphFromObject (temp graph creation)');
    // Use configurable depth: Infinity if loadAllFT is true, otherwise use lazyLoadDepth
    const maxDepth = canvasEnvironment.loadAllFT
        ? Infinity
        : (canvasEnvironment.lazyLoadDepth ?? LAZY_LOAD_INITIAL_DEPTH);

    // Get the current view strategy for FT expansion configuration
    const viewStrategy = faultTreeViewRegistry.get(canvasEnvironment.currentViewType);
    const isInitiatingEvent = element.get('data')?.type === 'INITIATING';
    // Use strategy hook to determine if horizontal anchors should be used
    const useHorizontalAnchors = viewStrategy.shouldUseHorizontalAnchors?.(canvasEnvironment, element) ?? false;

    // Build the fault tree graph
    profiler.mark('buildftGraphFromObject (temp graph creation)');
    const tempGraph = buildftGraphFromObject(
        canvasEnvironment.eventSequence!,
        element.get('data').faultTree,
        maxDepth,
        expandedLazyNodeIds,
        useHorizontalAnchors
    );

    // Get layout options from the view strategy
    profiler.mark('TreeLayout creation');
    const layoutOptions = viewStrategy.getFtExpansionLayoutOptions(isInitiatingEvent);
    const fTTreeLayout = new joint.layout.TreeLayout({
        graph: tempGraph,
        direction: layoutOptions.direction,
        siblingGap: layoutOptions.siblingGap,
        parentGap: layoutOptions.parentGap,
    });

    // Get all cells
    const cellsToAdd = tempGraph.getCells();

    // Find FT root and position it using the view strategy
    const [target] = tempGraph.getSources() as joint.dia.Element[];
    if (target) {
        viewStrategy.positionFtRoot(target, element, graph);
    }

    element.set({ collapsed: false });

    profiler.mark('paper.freeze + layoutAndFocus');
    paper.freeze();
    layoutAndFocus(fTTreeLayout, paperScroller, paper, viewportRect.center());

    profiler.mark('graph.addCells (temp → main graph)');
    graph.addCells(cellsToAdd);

    // Position barriers inside their containers after layout
    profiler.mark('positionBarriersInContainers');
    positionBarriersInContainers(graph);

    // Layout embedded barrier subtrees vertically (top to bottom)
    layoutEmbeddedBarrierSubtrees(graph);

    profiler.mark('Link creation + centerElement');
    if (target) {
        const link = BaseLink.create(element, target, undefined, { useHorizontalAnchors });
        link.attr({
            line: {
                stroke: '#000000',
                strokeWidth: 2,
                strokeDasharray: '5 5'
            }
        });
        link.set('id', 'link-' + element.get('id') + '-' + target.get('id'));
        graph.addCell(link);
        paperScroller.centerElement(target);
    }

    profiler.mark('resizePaper + paper.unfreeze');
    resizePaper(paper);

    profiler.end();

    // Track if afterRender has been called
    let renderComplete = false;

    // Use JointJS async rendering callback
    paper.unfreeze({
        progress: (done: boolean, _processed: number, _total: number) => {
            if (done && !renderComplete) {
                renderComplete = true;
                onRenderComplete();
            }
        }
    });

    // Fallback: if no cells to render, progress callback won't fire with done=true
    // Use RAF as fallback
    requestAnimationFrame(() => {
        if (!renderComplete) {
            renderComplete = true;
            onRenderComplete();
        }
    });

    function onRenderComplete(): void {
        // Guard: check if paper is still valid (not destroyed)
        if (!paper.el || !paper.el.parentNode) {
            canvasEnvironment.setLoading(false);
            return;
        }

        // Guard: check if a collapse or new expand has happened since this expand started
        // If so, skip the deferred decorations to avoid interfering with the new state
        if (canvasEnvironment.expansionGeneration !== currentGeneration) {
            canvasEnvironment.setLoading(false);
            return;
        }

        try {
            profiler.start(`Deferred decorations: ${ftName}`);
            profiler.mark('decorateVisibleElements');
            // Consolidated decoration in a single pass over visible elements
            decorateVisibleElements(canvasEnvironment, { addToolsFlag: true });
            profiler.mark('drawDnaPath');
            drawDnaPath(canvasEnvironment);
            profiler.mark('drawAllPsfs');
            drawAllPsfs(canvasEnvironment);
            profiler.end();
        } finally {
            // Hide loading spinner after decorations are done (even if error occurred)
            canvasEnvironment.setLoading(false);
        }
    }

    graph.translate(0.0001, 0.0001); // trick to force update of collapse/expand button status
}

/**
 * Expand a lazy-loaded node by creating its children on demand.
 * This is called when a node with hasUnloadedChildren is expanded.
 */
export function expandLazyNode(
    canvasEnvironment: CanvasEnvironment,
    parentElement: joint.dia.Element,
    graph: joint.dia.Graph,
    paper: joint.dia.Paper,
    paperScroller: joint.ui.PaperScroller,
    viewportRect: any
): void {
    const ftNode = parentElement.get('data') as FaultTreeNode;
    if (!ftNode || !ftNode.childIds || ftNode.childIds.length === 0) return;

    // Get the fault tree from the active top node (the event that owns this FT)
    const activeTopNode = canvasEnvironment.activeTopNode;
    if (!activeTopNode) return;

    const faultTree = activeTopNode.get('data').faultTree as FaultTree;
    if (!faultTree || !faultTree.faultTreeNodes) return;

    const eventSequence = canvasEnvironment.eventSequence;
    if (!eventSequence) return;

    // Use strategy to determine if horizontal anchors should be used
    const viewStrategy = faultTreeViewRegistry.get(canvasEnvironment.currentViewType);
    const useHorizontalAnchors = viewStrategy.shouldUseHorizontalAnchors?.(canvasEnvironment, parentElement) ?? false;

    const eventSequenceUniqueId = eventSequence.uniqueId;
    const quantificationMode = eventSequence.quantificationMode;

    // Build lookup map for FaultTreeNodes by id
    const ftNodeById = new Map<number, FaultTreeNode>();
    faultTree.faultTreeNodes.forEach((node) => {
        ftNodeById.set(node.id, node);
    });

    paper.freeze();

    // Create cells for direct children
    const newNodes: joint.dia.Cell[] = [];
    const newLinks: joint.dia.Cell[] = [];
    const nodeById = new Map<string, joint.dia.Cell>();

    for (const childId of ftNode.childIds) {
        const childFtNode = ftNodeById.get(childId);
        if (!childFtNode) continue;

        const nodeUniqueId = childFtNode.uniqueId.replace(eventSequenceUniqueId, '');
        const nodeType = childFtNode.nodeClass;
        const bias = childFtNode.bias;
        const pedigree = getPedigreeAbbrev(childFtNode.pedigree);
        const { label, qval } = getProbOrFrequencyValueGivenMode(quantificationMode, childFtNode);
        const qvalLabel = `${label}=${Number.parseFloat(qval.toString()).toExponential(2)}`;

        let node;
        if (nodeType === 'PRECURSOR') {
            node = Precursor.create(nodeUniqueId, childFtNode.name, qvalLabel, pedigree, `B=${bias}`, childFtNode.occurrenceCategory ?? '');
        } else if (nodeType === 'BARRIER') {
            node = Barrier.create(nodeUniqueId, childFtNode.name, qvalLabel, pedigree, `B=${bias}`, childFtNode.occurrenceCategory ?? '');
        } else {
            node = Node.create(nodeUniqueId, childFtNode.name, qvalLabel, pedigree, `B=${bias}`, childFtNode.occurrenceCategory ?? '');
        }
        if (childFtNode.type !== '') {
            node.gate(childFtNode.type.toLowerCase());
        }

        // Children have unloaded children if they have childIds
        const hasUnloadedChildren = childFtNode.childIds && childFtNode.childIds.length > 0;

        const nodeId = 'ft-' + childFtNode.id;
        node.set({
            data: childFtNode,
            id: nodeId,
            nodeClass: 'FAULTTREENODE',
            hasUnloadedChildren: hasUnloadedChildren,
            collapsed: hasUnloadedChildren
        });
        node.attr('body/title', childFtNode.name);

        newNodes.push(node);
        nodeById.set(nodeId, node);
    }

    // Add all new nodes to the graph first (needed for embedding)
    graph.addCells(newNodes);

    // Use strategy to create child links (handles barrier container logic)
    if (viewStrategy.createChildLinks) {
        const result = viewStrategy.createChildLinks(
            parentElement,
            ftNode.childIds,
            ftNodeById,
            nodeById,
            graph,
            useHorizontalAnchors
        );
        newLinks.push(...result.links);
    } else {
        // Default: link directly to children
        for (const childId of ftNode.childIds) {
            const nodeId = 'ft-' + childId;
            const child = nodeById.get(nodeId);
            if (child) {
                const link = BaseLink.create(parentElement, child, undefined, { useHorizontalAnchors });
                link.set('id', 'link-' + parentElement.get('id') + '-' + nodeId);
                newLinks.push(link);
            }
        }
    }

    // Add links to the graph
    graph.addCells(newLinks);

    // Mark parent as no longer having unloaded children
    parentElement.set({
        hasUnloadedChildren: false,
        collapsed: false
    });

    // Track this expansion for state restoration (per event)
    const eventId = activeTopNode.get('data')?.id as number;
    if (!canvasEnvironment.expandedLazyNodeIdsByEvent.has(eventId)) {
        canvasEnvironment.expandedLazyNodeIdsByEvent.set(eventId, new Set());
    }
    canvasEnvironment.expandedLazyNodeIdsByEvent.get(eventId)!.add(ftNode.id);

    // Re-layout the entire fault tree
    const ftGraph = new joint.dia.Graph({}, { cellNamespace: allshapes });
    const ftCells: Array<joint.dia.Cell> = [];
    graph.getElements().forEach((el) => {
        if (el.get('hidden')) return;
        const nodeClass = el.get('nodeClass');
        // Include FT nodes and BarrierContainers in layout
        if (nodeClass === 'FAULTTREENODE' || nodeClass === 'BARRIERCONTAINER') {
            // Skip elements embedded in a container (they're positioned relative to container)
            if (el.get('containerId')) return;
            ftCells.push(el);
        }
    });

    ftGraph.resetCells(graph.getSubgraph(ftCells));

    // Get layout options from the view strategy (viewStrategy already defined above)
    const isActiveTopNodeIE = canvasEnvironment.activeTopNode?.get('data')?.type === 'INITIATING';
    const layoutOptions = viewStrategy.getFtExpansionLayoutOptions(isActiveTopNodeIE);
    const fTTreeLayout = new joint.layout.TreeLayout({
        graph: ftGraph,
        direction: layoutOptions.direction,
        siblingGap: layoutOptions.siblingGap,
        parentGap: layoutOptions.parentGap,
    });

    layoutAndFocus(fTTreeLayout, paperScroller, paper, viewportRect.center());

    // Position barriers inside their containers after layout
    positionBarriersInContainers(graph);

    // Layout embedded barrier subtrees vertically (top to bottom)
    layoutEmbeddedBarrierSubtrees(graph);

    resizePaper(paper);
    paper.unfreeze();

    // Decorate visible elements after expansion
    decorateVisibleElements(canvasEnvironment, { addToolsFlag: true });

    graph.translate(0.0001, 0.0001); // Force update of collapse/expand button status
}

export function alterVisibilityOfBranchNodes(graph: joint.dia.Graph, element: joint.dia.Element): void {
    let successorElements: joint.dia.Cell[] = [];
    const nodeClass = element.get('nodeClass');

    if (nodeClass === 'FAULTTREENODE' || nodeClass === 'BARRIERCONTAINER') {
        // For FT nodes and BarrierContainers, get direct successors
        // Note: For BarrierContainer, embedded barriers should NOT be toggled - they stay visible
        // Only the non-barrier children (successors) are toggled
        successorElements = graph.getSuccessors(element);
    } else {
        // For event nodes, find the connected FT node and get its successors
        const [topFaultTreeNode] = graph.getNeighbors(element).filter((cell) => cell.get('nodeClass') === 'FAULTTREENODE');
        successorElements = [topFaultTreeNode, ...graph.getSuccessors(topFaultTreeNode)];
    }
    const [successor] = successorElements;

    if (!successor) {
        return;
    }
    const shouldExpand = !successor.get('hidden');
    const neighborElements = successorElements.filter(node =>
        node.isElement() && graph.isNeighbor(element, node as joint.dia.Element)
    );
    const successorCells = graph.getSubgraph([
        element,
        ...(shouldExpand ? successorElements : neighborElements)
    ]);
    successorCells.forEach((cell) => {
        if (cell === element) {
            cell.set({
                hidden: false,
                collapsed: shouldExpand,
            })
        } else {
            cell.set({ hidden: shouldExpand });
            if (cell.isElement()) {
                cell.set({ collapsed: !shouldExpand });

                // If this is a BarrierContainer being hidden/shown, also hide/show its embedded barriers
                // and their children (successors) and connected links
                if (cell.get('nodeClass') === 'BARRIERCONTAINER') {
                    const embeddedBarrierIds = cell.get('embeddedBarrierIds') as string[] | undefined;
                    if (embeddedBarrierIds) {
                        embeddedBarrierIds.forEach(barrierId => {
                            const nodeId = barrierId.startsWith('ft-') ? barrierId : 'ft-' + barrierId;
                            const barrierElement = graph.getCell(nodeId) as joint.dia.Element;
                            if (barrierElement) {
                                // When showing (expanding IE), reset embedded nodes to collapsed state
                                // When hiding (collapsing IE), just hide them
                                if (shouldExpand) {
                                    // Collapsing IE - hide embedded barrier
                                    barrierElement.set({ hidden: true });
                                } else {
                                    // Expanding IE - show embedded barrier but keep it collapsed
                                    barrierElement.set({ hidden: false, collapsed: true });
                                }

                                // Always hide the link from embedded barrier to its children
                                // (user must manually expand the embedded node)
                                const barrierLinks = graph.getConnectedLinks(barrierElement, { outbound: true });
                                barrierLinks.forEach(link => {
                                    link.set({ hidden: true });
                                });

                                // Always hide the successors (children) of embedded barriers
                                // (user must manually expand the embedded node)
                                const barrierSuccessors = graph.getSuccessors(barrierElement);
                                barrierSuccessors.forEach(successor => {
                                    successor.set({ hidden: true });

                                    // Hide links connected to successors
                                    const successorLinks = graph.getConnectedLinks(successor);
                                    successorLinks.forEach(link => {
                                        link.set({ hidden: true });
                                    });
                                });
                            }
                        });
                    }
                }
            }
        }
    });
}

export function toggleBranch(canvasEnvironment: CanvasEnvironment, element: joint.dia.Element, graph: joint.dia.Graph, paper: joint.dia.Paper, paperScroller: joint.ui.PaperScroller, viewportRect: any): void {
    profiler.start(`toggleBranch (${element.get('data')?.name || 'unknown'})`);

    profiler.mark('paper.freeze + alterVisibility');
    // Freeze BEFORE modifying visibility to prevent intermediate redraws
    paper.freeze();
    alterVisibilityOfBranchNodes(graph, element);

    profiler.mark('Collect visible FT cells');
    const ftGraph = new joint.dia.Graph({}, { cellNamespace: allshapes });
    const ftCells: Array<joint.dia.Cell> = [];

    // Collect IDs of all successors of embedded barriers (to exclude from main layout)
    const embeddedBarrierSuccessorIds = new Set<string>();
    graph.getElements().forEach((el) => {
        if (el.get('containerId')) {
            // This is an embedded barrier - collect its successors
            const successors = graph.getSuccessors(el);
            successors.forEach(s => embeddedBarrierSuccessorIds.add(s.id as string));
        }
    });

    graph.getElements().forEach((el) => {
        if (el.get('hidden')) return;
        const nodeClass = el.get('nodeClass');
        // Include FT nodes and BarrierContainers in layout
        if (nodeClass === 'FAULTTREENODE' || nodeClass === 'BARRIERCONTAINER') {
            // Skip elements embedded in a container (they're positioned relative to container)
            if (el.get('containerId')) return;
            // Skip successors of embedded barriers (they use vertical layout)
            if (embeddedBarrierSuccessorIds.has(el.id as string)) return;
            ftCells.push(el);
        }
    });

    profiler.mark('ftGraph.resetCells + getSubgraph');
    ftGraph.resetCells(graph.getSubgraph(ftCells));

    profiler.mark('TreeLayout creation');
    // Get layout options from the view strategy
    const viewStrategy = faultTreeViewRegistry.get(canvasEnvironment.currentViewType);
    const isActiveTopNodeIE = canvasEnvironment.activeTopNode?.get('data')?.type === 'INITIATING';
    const layoutOptions = viewStrategy.getFtExpansionLayoutOptions(isActiveTopNodeIE);
    const fTTreeLayout = new joint.layout.TreeLayout({
        graph: ftGraph,
        direction: layoutOptions.direction,
        siblingGap: layoutOptions.siblingGap,
        parentGap: layoutOptions.parentGap,
    });

    profiler.mark('layoutAndFocus');
    layoutAndFocus(fTTreeLayout, paperScroller, paper, viewportRect.center());

    // Position barriers inside their containers after layout
    positionBarriersInContainers(graph);

    // Layout embedded barrier subtrees vertically (top to bottom)
    layoutEmbeddedBarrierSubtrees(graph);

    profiler.mark('translate + paper.unfreeze');
    graph.translate(0.0001, 0.0001);  // trick to force update of collapse/expand button status
    paper.unfreeze();

    profiler.end();
}

export function setSelection(service: CanvasEnvironment, selection: joint.dia.Element[]) {
    const { paper, selection: previousSelection } = service;    
    previousSelection.forEach(cell => {
        const cellView = cell.findView(paper);
        if (cellView) {
            cellView.vel.removeClass('selected');
        }
    });
    service.selection = selection;
    selection.forEach(cell => {
        const cellView = cell.findView(paper);
        if (cellView) {
            cellView.vel.addClass('selected');
        }
    });
    const [element] = selection;
    // Let the top menu knows the selected node:
    // The Top menu needs to know in order to enable/disable menu items accordingly    
    service.setSelectedISAMNode(element ? element.get('data') : null);  
}

export function recenter(canvasEnvironment: CanvasEnvironment): void {
    const { graph, paperScroller } = canvasEnvironment;
    const [source] = graph.getSources();
    paperScroller.centerElement(source);
}

export function fitToView(canvasEnvironment: CanvasEnvironment): void {
    const { graph, paperScroller } = canvasEnvironment;
    const contentBBox = graph.getBBox();
    if (contentBBox) {
        paperScroller.zoomToFit({
            contentArea: contentBBox,
            padding: 50,
            minScale: 0.01,
            maxScale: 2,
            scaleGrid: 0.01,
            useModelGeometry: true
        });
    }
}

export function zoomIn(canvasEnvironment: CanvasEnvironment): void {
    const { paperScroller } = canvasEnvironment;
    paperScroller.zoom(0.1, { min: 0.01, max: 4, grid: 0.01 });
}

export function zoomOut(canvasEnvironment: CanvasEnvironment): void {
    const { paperScroller } = canvasEnvironment;
    paperScroller.zoom(-0.1, { min: 0.01, max: 4, grid: 0.01 });
}

export function fullScreen(canvasEnvironment: CanvasEnvironment) {
    if (canvasEnvironment.fullscreenCallback) {
        canvasEnvironment.fullscreenCallback();
    }
}

export function canEdit(env: CanvasEnvironment | null): boolean {
    if (!env || env.readonly) {
        return false;
    }
    if (!env.eventSequence || !env.eventSequence.group) {
        // Non-ESD container (e.g. a Component fault tree).
        return env.faultTreeContainer?.canEdit ?? false;
    }

    return CanvasUtils.canEdit(env.eventSequence);
}

export function canAddOrEditResource(env: CanvasEnvironment | null): boolean {
    if (!env || env.readonly) {
        return false;
    }
    if (!env.eventSequence || !env.eventSequence.group) {
        // Non-ESD container (e.g. a Component fault tree).
        return env.faultTreeContainer?.canEdit ?? false;
    }

    return CanvasUtils.canAddOrEditResource(env.eventSequence);
}

export function canPasteEvent(env: CanvasEnvironment | null, event: EsdEvent) {
    if (!canEdit(env)) {
        return false;
    }
    let type = event.type;
    let childIds = event.childIds;

    // If the user selects an END event, it can't be pasted here
    if (type === 'END') {
        return false;
    }

    // If the clipboard has no content then nothing can be pasted
    if (!localStorage.copyContents) {
        return false;
    }
    // If the copied object is something other than an event then it can't be pasted here
    const copyContents = JSON.parse(localStorage.copyContents);
    if (copyContents.type !== 'event') {
        return false
    }

    if (type === 'INITIATING') {
        // If the initiating event is full with 1 child then it can't be pasted here
        return !(childIds && childIds.length === 1);
    }

    if (type === 'PIVOTAL') {
        // If the pivotal event is full with 2 children then it can't be pasted here
        return !(childIds && childIds.length === 2);
    }

    return false
}

export function canPasteEventFaultTree(env: CanvasEnvironment | null, event: EsdEvent) {
    if (!canEdit(env)) {
        return false;
    }

    return CanvasUtils.canPasteFaultTree(event);
}

function findFaultTreeNode(
    events: EsdEvent[],
    predicate: (ftNode: FaultTreeNode) => boolean,
    graph: joint.dia.Graph,
    paper: joint.dia.Paper,
    filterFunc: (ftNodes: FaultTreeNode[]) => FaultTreeNode[],
    canvasEnvironment?: CanvasEnvironment
): joint.dia.Element | null {
    let foundElement: joint.dia.Element | null = null;

    // Helper function to expand an element if it's collapsed or has unloaded children
    const expandElement = (element: joint.dia.Element): void => {
        element.set('hidden', false);
        if (element.get('collapsed') || element.get('hasUnloadedChildren')) {
            paper.trigger('element:expand', paper.findViewByModel(element));
        }
    };

    // Helper to build path from root to target node
    const buildPathToNode = (faultTreeNodes: FaultTreeNode[], targetId: number): number[] => {
        // Build parent lookup map
        const parentMap = new Map<number, number>();
        for (const node of faultTreeNodes) {
            if (node.parentId) {
                parentMap.set(node.id, node.parentId);
            }
        }

        // Build path from target to root, then reverse
        const path: number[] = [];
        let currentId: number | undefined = targetId;
        while (currentId !== undefined) {
            path.push(currentId);
            currentId = parentMap.get(currentId);
        }
        return path.reverse(); // Now path is root -> target
    };

    // Iterate over events to find the fault tree node
    events.some(event => {
        if (event.faultTree) {
            const faultTreeNodes = filterFunc(event.faultTree.faultTreeNodes);
            return faultTreeNodes.some(ftNode => {
                if (predicate(ftNode)) {
                    const eventElement = graph.getCell(`event-${event.id}`) as joint.dia.Element;
                    if (!eventElement) {
                        return false;
                    }

                    // Check if the target node is already in the graph
                    let ftElement = graph.getCell(`ft-${ftNode.id}`) as joint.dia.Element;

                    if (!ftElement) {
                        // Node not loaded yet - need to expand the path to it
                        const pathToNode = buildPathToNode(event.faultTree.faultTreeNodes, ftNode.id);

                        // OPTIMIZATION: If we have canvasEnvironment, batch all expansions
                        // by adding path nodes to expandedLazyNodeIds and rebuilding once
                        if (canvasEnvironment && pathToNode.length > 0) {
                            const eventId = event.id;
                            if (!canvasEnvironment.expandedLazyNodeIdsByEvent.has(eventId)) {
                                canvasEnvironment.expandedLazyNodeIdsByEvent.set(eventId, new Set());
                            }
                            const expandedSet = canvasEnvironment.expandedLazyNodeIdsByEvent.get(eventId)!;

                            // Add all path nodes (except the target itself) to expanded set
                            // This ensures they'll be loaded when we rebuild
                            let needsRebuild = false;
                            for (const nodeId of pathToNode.slice(0, -1)) { // Exclude target node
                                if (!expandedSet.has(nodeId)) {
                                    expandedSet.add(nodeId);
                                    needsRebuild = true;
                                }
                            }

                            if (needsRebuild) {
                                if (!eventElement.get('collapsed')) {
                                    // Event already expanded - force rebuild with new expanded nodes
                                    const viewportRect = canvasEnvironment.paperScroller.getVisibleArea().inflate(50);
                                    createFtGraphAndExpand(canvasEnvironment, eventElement, graph, paper, canvasEnvironment.paperScroller, viewportRect);
                                } else {
                                    // Expand the event element (this triggers createFtGraphAndExpand)
                                    expandElement(eventElement);
                                }

                                // Now try to get the element again
                                ftElement = graph.getCell(`ft-${ftNode.id}`) as joint.dia.Element;
                            }
                        } else {
                            // Fallback: expand event first, then expand path nodes one by one
                            expandElement(eventElement);

                            // Expand each node along the path until we reach the target
                            for (const nodeId of pathToNode) {
                                const pathElement = graph.getCell(`ft-${nodeId}`) as joint.dia.Element;
                                if (pathElement) {
                                    if (pathElement.get('hasUnloadedChildren')) {
                                        expandElement(pathElement);
                                    }
                                }
                            }

                            // Try to get the element again after expanding the path
                            ftElement = graph.getCell(`ft-${ftNode.id}`) as joint.dia.Element;
                        }
                    } else {
                        // Node exists but event might be collapsed - expand it
                        expandElement(eventElement);
                    }

                    if (!ftElement) {
                        // Still not loaded - return event element as fallback
                        foundElement = eventElement;
                        return true;
                    }

                    // Expand all predecessors if the fault tree node is collapsed
                    if (ftElement.get('collapsed') || ftElement.get('hidden')) {
                        const predecessors = graph.getPredecessors(ftElement).reverse();
                        predecessors.forEach(predecessor => expandElement(predecessor));
                    }

                    foundElement = ftElement;
                    return true;
                }
                return false;
            });
        }
        return false;
    });

    return foundElement;
}

function searchFaultTreeNodes(env: CanvasEnvironment, query: string): joint.dia.Element | null {
    let currentNode = env.selection[0]?.get('data');
    let allEvents = env.eventSequence?.events ?? [];

    // Helper function to find the index of the current fault tree branch
    const findCurrentFtBranchIndex = (events: EsdEvent[], currentNode: any) => {
        return (currentNode && CanvasUtils.isFaultTreeNode(currentNode.type)) ?
            events.findIndex(event => currentNode.uniqueId.includes(event.uniqueId))
            : -1;
    };

    // Helper function to find the index of the current fault tree node
    const findCurrentFaultTreeNodeIndex = (faultTreeNodes: any[], currentNode: any) => {
        return (currentNode && CanvasUtils.isFaultTreeNode(currentNode.type)) ?
            faultTreeNodes.findIndex(ftNode => currentNode.id === ftNode.id)
            : -1;
    };


    // Adjust the events array cursor based on the current node 
    // so that the search starts from the current FT branch
    const currentFtBranchIndex = findCurrentFtBranchIndex(allEvents, currentNode);
    if (currentFtBranchIndex !== -1) {
        allEvents = allEvents.slice(currentFtBranchIndex);
    }

    const FilterFunc = (ftNodes: FaultTreeNode[]): FaultTreeNode[] => {
        const currentFaultTreeNodeIndex = findCurrentFaultTreeNodeIndex(ftNodes, currentNode);
        if (currentFaultTreeNodeIndex !== -1) {
            return ftNodes.slice(currentFaultTreeNodeIndex + 1);
        }
        return ftNodes;
    }

    const predicateFunc = (ftNode: FaultTreeNode) => {
        return ftNode.name.toLowerCase().includes(query.toLowerCase()) || ftNode.uniqueId.toLowerCase().includes(query.toLowerCase());
    }

    // Search through the fault tree nodes
    return findFaultTreeNode(allEvents, predicateFunc, env.graph, env.paper,
        FilterFunc, env);
}

interface TagNamed {
    name: string;
    id: number;
    type: 'event' | 'faultTreeNode';
    nodeId: number;
}

/**
 * Represents a search result for the autocomplete dropdown
 */
export interface SearchResult {
    id: string;          // element id (e.g., "event-123" or "ft-456") or "more-results" for indicator
    uniqueId: string;    // node uniqueId for display
    name: string;        // node name for display
    type: 'event' | 'faultTreeNode' | 'indicator';
    moreCount?: number;  // number of additional results not shown (only for indicator type)
    tags?: string[];     // matching tags (only for tag search results)
    hasMoreTags?: number; // number of additional tags not shown
    matchedInDescription?: boolean; // true if match was found in description field
    faultTreeName?: string; // fault tree name prefix to strip from uniqueId for display
}

const MAX_SEARCH_RESULTS = 50;

/**
 * Sort search results by relevance:
 * 1. Exact uniqueId match
 * 2. UniqueId starts with query
 * 3. Exact name match
 * 4. Name starts with query
 * 5. UniqueId contains query
 * 6. Name contains query
 * 7. Description contains query (lowest priority)
 */
function sortByRelevance(results: SearchResult[], query: string): SearchResult[] {
    const queryLower = query.toLowerCase();

    return results.sort((a, b) => {
        const aUniqueIdLower = a.uniqueId.toLowerCase();
        const bUniqueIdLower = b.uniqueId.toLowerCase();
        const aNameLower = a.name.toLowerCase();
        const bNameLower = b.name.toLowerCase();

        // Score: lower is better
        const getScore = (uniqueId: string, name: string, matchedInDescription?: boolean): number => {
            if (uniqueId === queryLower) return 0;           // Exact uniqueId match
            if (uniqueId.startsWith(queryLower)) return 1;   // UniqueId starts with
            if (name === queryLower) return 2;               // Exact name match
            if (name.startsWith(queryLower)) return 3;       // Name starts with
            if (uniqueId.includes(queryLower)) return 4;     // UniqueId contains
            if (name.includes(queryLower)) return 5;         // Name contains
            if (matchedInDescription) return 6;              // Description contains
            return 7;                                        // Fallback
        };

        const scoreA = getScore(aUniqueIdLower, aNameLower, a.matchedInDescription);
        const scoreB = getScore(bUniqueIdLower, bNameLower, b.matchedInDescription);

        if (scoreA !== scoreB) return scoreA - scoreB;

        // Tiebreaker: alphabetical by uniqueId
        return aUniqueIdLower.localeCompare(bUniqueIdLower);
    });
}

/**
 * Limit results and add indicator if there are more
 */
function limitResults(results: SearchResult[], query: string): SearchResult[] {
    const sorted = sortByRelevance(results, query);

    if (sorted.length <= MAX_SEARCH_RESULTS) {
        return sorted;
    }

    const limited = sorted.slice(0, MAX_SEARCH_RESULTS);
    const moreCount = sorted.length - MAX_SEARCH_RESULTS;

    // Add indicator for more results
    limited.push({
        id: 'more-results',
        uniqueId: '',
        name: `${moreCount} more result${moreCount > 1 ? 's' : ''}. Refine your search.`,
        type: 'indicator',
        moreCount
    });

    return limited;
}

/**
 * Search all nodes (events and fault tree nodes) that match the query.
 * Returns matches sorted by relevance, limited to MAX_SEARCH_RESULTS.
 */
export function searchAllNodes(env: CanvasEnvironment | null, query: string): SearchResult[] {
    if (!env || !query || query.length < 2) {
        return [];
    }

    const results: SearchResult[] = [];
    const events = env.eventSequence?.events ?? [];
    const queryLower = query.toLowerCase();

    // Search events (by uniqueId, name, or description)
    events.forEach(event => {
        const matchesUniqueId = event.uniqueId.toLowerCase().includes(queryLower);
        const matchesName = event.name.toLowerCase().includes(queryLower);
        const matchesDescription = Boolean(event.description && event.description.toLowerCase().includes(queryLower));

        if (matchesUniqueId || matchesName || matchesDescription) {
            results.push({
                id: `event-${event.id}`,
                uniqueId: event.uniqueId,
                name: event.name,
                type: 'event',
                matchedInDescription: !matchesUniqueId && !matchesName && matchesDescription,
                faultTreeName: env.eventSequence?.name
            });
        }

        // Search fault tree nodes within each event (by uniqueId, name, or description)
        if (event.faultTree && event.faultTree.faultTreeNodes) {
            event.faultTree.faultTreeNodes.forEach((ftNode: FaultTreeNode) => {
                const ftMatchesUniqueId = ftNode.uniqueId.toLowerCase().includes(queryLower);
                const ftMatchesName = ftNode.name.toLowerCase().includes(queryLower);
                const ftMatchesDescription = Boolean(ftNode.description && ftNode.description.toLowerCase().includes(queryLower));

                if (ftMatchesUniqueId || ftMatchesName || ftMatchesDescription) {
                    results.push({
                        id: `ft-${ftNode.id}`,
                        uniqueId: ftNode.uniqueId,
                        name: ftNode.name,
                        type: 'faultTreeNode',
                        matchedInDescription: !ftMatchesUniqueId && !ftMatchesName && ftMatchesDescription,
                        faultTreeName: env.eventSequence?.name
                    });
                }
            });
        }
    });

    return limitResults(results, query);
}

/**
 * Navigate to a specific node by element id and highlight it.
 * Expands collapsed parents if needed.
 */
export function navigateToNode(env: CanvasEnvironment | null, elementId: string): void {
    if (!env || !elementId) {
        return;
    }

    const { graph, paper, HighlighterFrame } = env;

    // Clear previous highlights
    HighlighterFrame.removeAll(paper);

    // Force synchronous expansion during navigation (no loading spinner)
    env.isRestoringState = true;

    try {
        // Check if it's a fault tree node
        if (elementId.startsWith('ft-')) {
            const nodeId = parseInt(elementId.replace('ft-', ''), 10);
            const events = env.eventSequence?.events ?? [];

            // Find the fault tree node and its parent event
            for (const event of events) {
                if (event.faultTree && event.faultTree.faultTreeNodes) {
                    const ftNode = event.faultTree.faultTreeNodes.find((n: FaultTreeNode) => n.id === nodeId);
                    if (ftNode) {
                        // Use predicate to find and expand the node
                        const predicateFunc = (node: FaultTreeNode): boolean => node.id === nodeId;
                        const element = findFaultTreeNode(events, predicateFunc, graph, paper, (ftNodes) => ftNodes, env);
                        if (element) {
                            highlightElement(element, env);
                        }
                        return;
                    }
                }
            }
        } else {
            // It's an event node
            const element = graph.getCell(elementId) as joint.dia.Element;
            if (element) {
                highlightElement(element, env);
            }
        }
    } finally {
        env.isRestoringState = false;
    }
}

/**
 * Centres, frames and selects one fault tree node on a canvas that draws a whole tree at once, as
 * the Component canvas does.
 *
 * {@link navigateToNode}'s fault-tree branch cannot serve that canvas: it looks the node up by
 * walking an event sequence's events to find the event owning it, then expands that event to reveal
 * it. A Component has no events, and its tree is built to unlimited depth with nothing collapsed,
 * so the cell is already on the graph and only needs finding.
 *
 * @param env       the canvas environment holding the graph
 * @param elementId the cell id, in the `ft-{nodeId}` form the lint findings link with
 */
export function navigateToFaultTreeNode(env: CanvasEnvironment | null, elementId: string): void {
    if (!env || !elementId) {
        return;
    }
    const { graph, paper, HighlighterFrame } = env;
    HighlighterFrame.removeAll(paper);
    env.isRestoringState = true;
    try {
        const element = graph.getCell(elementId) as joint.dia.Element;
        if (element) {
            highlightElement(element, env);
        }
    } finally {
        env.isRestoringState = false;
    }
}

/**
 * Search all nodes that have a tag matching the query.
 * Returns deduplicated matches with combined tags for display in autocomplete dropdown.
 */
export function searchAllNodesByTag(env: CanvasEnvironment | null, tagName: string): SearchResult[] {
    if (!env || !tagName || tagName.length < 2) {
        return [];
    }

    const events = env.eventSequence?.events ?? [];
    const tagNameLower = tagName.toLowerCase();

    // Map to collect matching tags per node: nodeId -> { details, tags[] }
    const nodeTagsMap = new Map<string, { uniqueId: string; name: string; type: 'event' | 'faultTreeNode'; tags: string[] }>();

    // Helper to find node details by id
    const findEventById = (eventId: number): { uniqueId: string; name: string } | null => {
        const event = events.find(e => e.id === eventId);
        return event ? { uniqueId: event.uniqueId, name: event.name } : null;
    };

    const findFaultTreeNodeById = (nodeId: number): { uniqueId: string; name: string } | null => {
        for (const event of events) {
            if (event.faultTree?.faultTreeNodes) {
                const ftNode = event.faultTree.faultTreeNodes.find((n: FaultTreeNode) => n.id === nodeId);
                if (ftNode) {
                    return { uniqueId: ftNode.uniqueId, name: ftNode.name };
                }
            }
        }
        return null;
    };

    const eventSequenceName = env.eventSequence?.name;

    // Search event tags
    if (env.esdTagLinks?.eventTags) {
        for (const tagLink of env.esdTagLinks.eventTags) {
            // Use pre-built Map for O(1) lookup instead of O(n) .find()
            const tag = env.tagById.get(tagLink.tagId);
            if (tag && tag.name.toLowerCase().includes(tagNameLower)) {
                const nodeId = `event-${tagLink.eventId}`;
                const existing = nodeTagsMap.get(nodeId);
                if (existing) {
                    existing.tags.push(tag.name);
                } else {
                    const eventDetails = findEventById(tagLink.eventId);
                    if (eventDetails) {
                        nodeTagsMap.set(nodeId, {
                            uniqueId: eventDetails.uniqueId,
                            name: eventDetails.name,
                            type: 'event',
                            tags: [tag.name]
                        });
                    }
                }
            }
        }
    }

    // Search fault tree node tags
    if (env.esdTagLinks?.faultTreeNodeTags) {
        for (const tagLink of env.esdTagLinks.faultTreeNodeTags) {
            // Use pre-built Map for O(1) lookup instead of O(n) .find()
            const tag = env.tagById.get(tagLink.tagId);
            if (tag && tag.name.toLowerCase().includes(tagNameLower)) {
                const nodeId = `ft-${tagLink.faultTreeNodeId}`;
                const existing = nodeTagsMap.get(nodeId);
                if (existing) {
                    existing.tags.push(tag.name);
                } else {
                    const ftNodeDetails = findFaultTreeNodeById(tagLink.faultTreeNodeId);
                    if (ftNodeDetails) {
                        nodeTagsMap.set(nodeId, {
                            uniqueId: ftNodeDetails.uniqueId,
                            name: ftNodeDetails.name,
                            type: 'faultTreeNode',
                            tags: [tag.name]
                        });
                    }
                }
            }
        }
    }

    // Convert map to results array with tags info
    const MAX_DISPLAY_TAGS = 2;
    const results: SearchResult[] = [];
    nodeTagsMap.forEach((node, nodeId) => {
        const displayTags = node.tags.slice(0, MAX_DISPLAY_TAGS);
        const hasMoreTags = node.tags.length > MAX_DISPLAY_TAGS ? node.tags.length - MAX_DISPLAY_TAGS : undefined;
        results.push({
            id: nodeId,
            uniqueId: node.uniqueId,
            name: node.name,
            type: node.type,
            tags: displayTags,
            hasMoreTags,
            faultTreeName: eventSequenceName
        });
    });

    return limitResults(results, tagName);
}

function searchTags(env: CanvasEnvironment, tagName: string) {
    const { graph, paper, HighlighterFrame } = env;
    const tagNameLower = tagName.toLowerCase();

    // Helper function to find tags by name - uses pre-built Map for O(1) lookup
    const findTags = (tagLinks: any[], type: 'event' | 'faultTreeNode') => {
        return tagLinks.map(tagLink => {
            const tag = env.tagById.get(tagLink.tagId);
            if (tag && tag.name.toLowerCase().includes(tagNameLower)) {
                return { name: tag.name, id: tag.id!, type, nodeId: type === 'event' ? tagLink.eventId : tagLink.faultTreeNodeId };
            }
            return null;
        }).filter(tag => tag !== null) as TagNamed[];
    };

    HighlighterFrame.removeAll(paper);

    // Find all matching tags
    const eventTags = findTags(env.esdTagLinks.eventTags, 'event');
    const faultTreeNodeTags = findTags(env.esdTagLinks.faultTreeNodeTags, 'faultTreeNode');
    const allTags = [...eventTags, ...faultTreeNodeTags];
    if (allTags.length > 0) {
        // the search starts from the current selected node, 
        // otherwise from the beginning
        let currentNode = env.selection[0]?.get('data');
        let tag = allTags[0];
        if (currentNode) {
            // Find the index of the current node in the allTags array
            const currentIndex = allTags.findIndex((tag) => tag.nodeId === currentNode.id);

            // If the current node is found, search for the next matching tag
            if (currentIndex !== undefined && currentIndex !== -1) {
                // Use pre-computed tagNameLower instead of calling toLowerCase() in loop
                let nextTag = allTags.slice(currentIndex + 1).find((t) => t.name.toLowerCase().includes(tagNameLower));
                if (nextTag) {
                    tag = nextTag;
                }
            }
        }
        let predicateFunc = (ftNode: FaultTreeNode): boolean => {
            return ftNode.id === tag.nodeId;
        }

        let element = tag.type === 'event' ? graph.getCell('event-' + tag.nodeId) as joint.dia.Element : findFaultTreeNode(env.eventSequence?.events ?? [], predicateFunc, env.graph, env.paper, (ftNodes) => ftNodes, env);
        if (element)
            highlightElement(element, env);
    }
}

// Helper function to highlight a tag
function highlightElement(element: joint.dia.Element, env: CanvasEnvironment) {
    const { paper, paperScroller, HighlighterFrame } = env;
    HighlighterFrame.add(element.findView(paper), 'body', 'frame', { padding: 10 });
    paperScroller.centerElement(element);
    setSelection(env, [element]);
}

export function search(env: CanvasEnvironment | null, query: string | null) {
    if (!env || !query) {
        return;
    }

    // Search for tags if query includes 'tag:'
    if (query.includes('tag:')) {
        searchTags(env, query.split(':')[1].trim());
        return;
    }

    // Helper function to find an event by query
    const findEvent = (events: EsdEvent[], query: string) => {
        return events.find(event => 
            event.name.toLowerCase().includes(query.toLowerCase()) || 
            event.uniqueId.toLowerCase().includes(query.toLowerCase())
        );
    };
    
    const { graph, paper,  HighlighterFrame } = env;
    let currentNode = env.selection[0]?.get('data');
    const events = env.eventSequence?.events ?? [];
    
    
    let foundEvent = null;

    if (currentNode) {
        // If the current node is an event, search for the next matching event
        if (CanvasUtils.isEvent(currentNode.type)) {
            let currentIndex = events.findIndex((event) => event.id === currentNode.id);
            if (currentIndex !== -1) {
                const nextEvents = events.slice(currentIndex + 1);
                let nextEvent = findEvent(nextEvents, query);
                if (nextEvent) {
                    foundEvent = nextEvent;
                }
            }
        }
    } else
        foundEvent = findEvent(events, query);

    HighlighterFrame.removeAll(paper);
    if (foundEvent) {
        let element = graph.getCell('event-' + foundEvent.id) as joint.dia.Element;
        highlightElement(element, env);
    } else {
        let element = searchFaultTreeNodes(env, query);    
        if (element) {
            highlightElement(element, env);
        } else if (currentNode) {
            env.HighlighterFrame.removeAll(env.paper);
            setSelection(env, []);
            search(env, query);
        }
    }
}

export function clearSearch(env: CanvasEnvironment | null) {
    if (!env) {
        return;
    }
    const { HighlighterFrame } = env;
    HighlighterFrame.clear();
}

export function getTopLeftPaddingOfElement(env: CanvasEnvironment, element: joint.dia.Element) {
    let { x, y } = element.position().difference(env.paperScroller.getVisibleArea().topLeft());
    let { sx, sy } = env.paper.scale();
    return { left: x * sx, top: y * sy };
}

export function getTopLeftPaddingOfElementAndParent(env: CanvasEnvironment, element: joint.dia.Element) {
    let { x, y } = element.position().difference(env.paperScroller.getVisibleArea().topLeft());
    let { sx, sy } = env.paper.scale();
    let [parent] = env.graph.getPredecessors(element);
    if (parent) {
        let { x: px, y: py } = parent.position().difference(env.paperScroller.getVisibleArea().topLeft());
        return { left: x * sx, top: y * sy, parentLeft: px * sx, parentTop: py * sy };
    }
    return { left: x * sx, top: y * sy , parentLeft: 200, parentTop: 50};
}

export function getDNAPath(graph: joint.dia.Graph, lowerElement: joint.dia.Element, topElement: joint.dia.Element): joint.dia.Element[] {
    let pathElements: joint.dia.Element[] = [];
    let shortestPath = joint.graphUtils.shortestPath(graph, lowerElement, topElement);
    shortestPath.forEach((element) => {
        let node = graph.getCell(element) as joint.dia.Element;
        const nodeClass = node.get('nodeClass');
        if (nodeClass === 'FAULTTREENODE' || nodeClass === 'EVENTNODE') {
            pathElements.push(node);
        } else if (nodeClass === 'BARRIERCONTAINER') {
            // When path goes through a BarrierContainer, include all its embedded barriers
            const embeddedBarrierIds = node.get('embeddedBarrierIds') as string[] | undefined;
            if (embeddedBarrierIds) {
                embeddedBarrierIds.forEach((barrierId: string) => {
                    const nodeId = barrierId.startsWith('ft-') ? barrierId : 'ft-' + barrierId;
                    const barrierNode = graph.getCell(nodeId) as joint.dia.Element;
                    if (barrierNode && !barrierNode.get('hidden')) {
                        pathElements.push(barrierNode);
                    }
                });
            }
        }
    });


    if (pathElements.length > 0) {
        let lastnode = topElement;
        if (lastnode.get('nodeClass') === 'FAULTTREENODE' && (lastnode.get('data').nodeClass === 'BARRIER') || (lastnode.get('data').nodeClass === 'CIRCUMSTANTIAL')) {
            let nodeParent = graph.getNeighbors(lastnode, { inbound: true })[0];
            if (nodeParent  && nodeParent.get('data').nodeClass === 'PRECURSOR') {
                let idx = pathElements.findIndex((element) => element === nodeParent);
                if (idx >= 0) {
                    pathElements.splice(idx, 1);
                }
            }
        }

        let more: joint.dia.Element[] = [];
        pathElements.slice(1,-1).forEach((element) => {
            if (element.get('nodeClass') === 'FAULTTREENODE' && element.get('data').type === 'AND') {
                let children = graph.getNeighbors(element, { outbound: true });
                children.forEach((child) => {
                    let idx = more.findIndex((el) => el === child);
                    if (idx === -1) {
                        more.push(child);
                    }
                });
            }
            more.push(element);
        });
        more.unshift(lowerElement);
        more.push(topElement);
        return more;
    }

    return pathElements;

}

export function isBarrierDescendant(canvasEnvironment: CanvasEnvironment | null, selected: joint.dia.Element | null): boolean {
    if (!canvasEnvironment) return false;
    if (!selected) return false;
    for (let element of canvasEnvironment.graph.getPredecessors(selected)) {
        if (element.get('nodeClass') === 'FAULTTREENODE' && element.get('data').nodeClass === 'BARRIER') {
            return true;
        }
        if (element.get('nodeClass') === 'FAULTTREENODE' && element.get('data').nodeClass !== 'NODE') {
            return false;
        }
    }
    return false;
}

